import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { listMembers, leaveClub, removeMember, changeMemberRole } from '../../api/clubs';
import type { ClubMemberResponse, ClubRole } from '../../types/api';
import { canManageMembers, isOwner, canLogForOthers } from '../../lib/permissions';
import { RoleBadge } from './RoleBadge';
import { ConfirmDialog } from '../ConfirmDialog';
import { TransferOwnershipDialog } from './TransferOwnershipDialog';
import { AddMemberDialog } from './AddMemberDialog';
import { initials } from '../../lib/avatar';

interface Props {
  clubId: number;
  role: ClubRole;
  isSuperAdmin: boolean;
  currentUserId: string;
  onLeave: () => void;
}

type ConfirmState =
  | { kind: 'leave' }
  | { kind: 'remove'; target: ClubMemberResponse }
  | { kind: 'role'; target: ClubMemberResponse; newRole: ClubRole }
  | null;

export function MembersList({ clubId, role, isSuperAdmin, currentUserId, onLeave }: Props) {
  const [members, setMembers] = useState<ClubMemberResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [pending, setPending] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<ConfirmState>(null);
  const [transferOpen, setTransferOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);

  const canManage = canManageMembers(role, isSuperAdmin);
  const callerIsOwner = isOwner(role, isSuperAdmin);
  const navigate = useNavigate();
  const canLog = canLogForOthers(role, isSuperAdmin);

  function refetch() {
    setLoading(true);
    setError(null);
    listMembers(clubId)
      .then(setMembers)
      .catch((err: unknown) => setError(err instanceof Error ? err.message : 'Failed to load members'))
      .finally(() => setLoading(false));
  }

  useEffect(refetch, [clubId]);

  async function handleLeave() {
    setPending(currentUserId);
    setActionError(null);
    try {
      await leaveClub(clubId);
      onLeave();
    } catch (err: unknown) {
      setActionError(err instanceof Error ? err.message : 'Failed to leave club');
      setPending(null);
    }
  }

  async function handleRemove(target: ClubMemberResponse) {
    setPending(target.userId);
    setActionError(null);
    try {
      await removeMember(clubId, target.userId);
      setMembers(prev => prev.filter(m => m.userId !== target.userId));
    } catch (err: unknown) {
      setActionError(err instanceof Error ? err.message : 'Failed to remove member');
    } finally {
      setPending(null);
      setConfirm(null);
    }
  }

  async function handleRoleChange(target: ClubMemberResponse, newRole: ClubRole) {
    setPending(target.userId);
    setActionError(null);
    try {
      const updated = await changeMemberRole(clubId, target.userId, newRole);
      setMembers(prev => prev.map(m => m.userId === target.userId ? updated : m));
    } catch (err: unknown) {
      setActionError(err instanceof Error ? err.message : 'Failed to change role');
    } finally {
      setPending(null);
      setConfirm(null);
    }
  }

  const ownerMember = members.find(m => m.role === 'Owner');
  const ownerExists = ownerMember !== undefined;
  const otherMembers = members.filter(m => m.role !== 'Owner');
  const ordered = ownerMember ? [ownerMember, ...otherMembers] : members;

  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5 shadow-sm">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-base font-semibold text-slate-900 dark:text-slate-50">Members</h2>
        <div className="flex items-center gap-3">
          {canManage && (
            <button
              type="button"
              onClick={() => setAddOpen(true)}
              className="text-xs font-medium text-teal-700 dark:text-teal-400 hover:underline"
            >
              Add member
            </button>
          )}
          {callerIsOwner && ownerExists && (
            <button
              type="button"
              onClick={() => setTransferOpen(true)}
              className="text-xs font-medium text-amber-700 dark:text-amber-400 hover:underline"
            >
              Transfer ownership
            </button>
          )}
        </div>
      </div>

      {loading && (
        <div className="space-y-2">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-10 animate-pulse rounded-lg bg-slate-100 dark:bg-slate-700" />
          ))}
        </div>
      )}
      {error && <p className="text-sm text-red-500">{error}</p>}
      {actionError && <p className="text-sm text-red-500 mt-2">{actionError}</p>}

      {!loading && !error && members.length === 0 && (
        <p className="text-sm text-slate-500 dark:text-slate-400">No members yet</p>
      )}

      {!loading && !error && members.length > 0 && (
        <ul className="divide-y divide-slate-100 dark:divide-slate-700">
          {ordered.map((member) => {
            const isSelf = member.userId === currentUserId;
            const isOwnerRow = member.role === 'Owner';
            const otherMembersExist = members.length > 1;
            const leaveDisabled = isSelf && member.role === 'Owner' && otherMembersExist;

            return (
              <li key={member.userId} className="flex items-center gap-3 py-2.5">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-teal-100 dark:bg-teal-900/40 text-xs font-bold text-teal-700 dark:text-teal-400">
                  {initials(member.abbreviation, member.displayName)}
                </div>
                <div className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-slate-900 dark:text-slate-100">{member.displayName}</span>
                </div>

                {canManage && !isOwnerRow ? (
                  <select
                    value={member.role}
                    onChange={(e) => {
                      const newRole = e.target.value as ClubRole;
                      if (newRole !== member.role) {
                        setConfirm({ kind: 'role', target: member, newRole });
                      }
                    }}
                    disabled={pending === member.userId}
                    className="rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-2 py-1 text-xs text-slate-900 dark:text-slate-100 cursor-pointer transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <option value="Admin">Admin</option>
                    <option value="Member">Member</option>
                    <option value="Guest">Guest</option>
                  </select>
                ) : (
                  <RoleBadge role={member.role} />
                )}

                {isSelf && (
                  <button
                    type="button"
                    onClick={() => setConfirm({ kind: 'leave' })}
                    disabled={pending === currentUserId || leaveDisabled}
                    title={leaveDisabled ? 'Transfer ownership before leaving' : undefined}
                    className="shrink-0 text-xs text-red-500 hover:text-red-700 dark:hover:text-red-400 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer transition-colors"
                  >
                    {pending === currentUserId ? 'Leaving…' : 'Leave'}
                  </button>
                )}
                {canLog && !isSelf && member.role !== 'Guest' && (
                  <button
                    type="button"
                    onClick={() =>
                      navigate(`/log-catch?onBehalfOf=${encodeURIComponent(member.userId)}&clubId=${clubId}`)
                    }
                    className="shrink-0 text-xs text-teal-600 hover:text-teal-700 dark:text-teal-400 dark:hover:text-teal-300 cursor-pointer transition-colors"
                  >
                    Log catch
                  </button>
                )}
                {canManage && !isSelf && !isOwnerRow && (
                  <button
                    type="button"
                    onClick={() => setConfirm({ kind: 'remove', target: member })}
                    disabled={pending === member.userId}
                    className="shrink-0 text-xs text-red-500 hover:text-red-700 dark:hover:text-red-400 disabled:opacity-50 cursor-pointer transition-colors"
                  >
                    {pending === member.userId ? 'Removing…' : 'Remove'}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <ConfirmDialog
        open={confirm?.kind === 'leave'}
        title="Leave club?"
        description="You will lose access to this club. You can re-join with an invite code."
        confirmLabel="Leave"
        confirmTone="danger"
        busy={pending === currentUserId}
        onConfirm={handleLeave}
        onCancel={() => setConfirm(null)}
      />

      <ConfirmDialog
        open={confirm?.kind === 'remove'}
        title="Remove member?"
        description={
          confirm?.kind === 'remove'
            ? `Remove ${confirm.target.displayName} from this club?`
            : ''
        }
        confirmLabel="Remove"
        confirmTone="danger"
        busy={confirm?.kind === 'remove' && pending === confirm.target.userId}
        onConfirm={() => confirm?.kind === 'remove' && handleRemove(confirm.target)}
        onCancel={() => setConfirm(null)}
      />

      <ConfirmDialog
        open={confirm?.kind === 'role'}
        title="Change role?"
        description={
          confirm?.kind === 'role'
            ? `Change ${confirm.target.displayName} from ${confirm.target.role} to ${confirm.newRole}?`
            : ''
        }
        confirmLabel="Change"
        busy={confirm?.kind === 'role' && pending === confirm.target.userId}
        onConfirm={() =>
          confirm?.kind === 'role' && handleRoleChange(confirm.target, confirm.newRole)
        }
        onCancel={() => setConfirm(null)}
      />

      {ownerMember && (
        <TransferOwnershipDialog
          clubId={clubId}
          members={members}
          currentOwnerUserId={ownerMember.userId}
          open={transferOpen}
          onClose={() => setTransferOpen(false)}
          onTransferred={() => {
            setTransferOpen(false);
            refetch();
          }}
        />
      )}

      <AddMemberDialog
        clubId={clubId}
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onAdded={(member) => {
          setAddOpen(false);
          setMembers(prev => [...prev, member]);
        }}
      />
    </div>
  );
}
